import { StateCreator } from 'zustand';
import { googleTasksService } from '../../services/googleTasksService';
import { buildImportCandidates, ImportCandidate } from '../../domain';
import type { AppStore } from '../index';

/** Tags every imported chore so it can be traced back — and found again if an import goes wrong. */
export const IMPORT_LABEL = 'google-tasks';

export interface ImportOutcome {
  imported: number;
  failures: { title: string; error: string }[];
}

export interface GoogleTasksSlice {
  googleTasks: {
    candidates: ImportCandidate[];
    scanning: boolean;
    scanned: boolean;
    error: string | null;
    /** Totals from the last scan, shown so an empty result is distinguishable from a failed one. */
    listCount: number;
    taskCount: number;
    /** Lists whose history was cut short by the page cap — the scan is incomplete for these. */
    truncatedLists: string[];
    /** Candidate keys ticked for import. */
    selectedKeys: string[];
    /** Candidate keys already turned into chores this session — guards against double-import. */
    importedKeys: string[];
    importing: boolean;
    importResult: ImportOutcome | null;
    scan: () => Promise<void>;
    reset: () => void;
    clearError: () => void;
    toggleSelection: (key: string) => void;
    /** Bulk select/deselect, for the per-section "select all" control. */
    setSelection: (keys: string[], selected: boolean) => void;
    clearSelection: () => void;
    importSelected: (householdId: string) => Promise<void>;
    dismissImportResult: () => void;
  };
}

export const createGoogleTasksSlice: StateCreator<AppStore, [], [], GoogleTasksSlice> = (set, get) => ({
  googleTasks: {
    candidates: [],
    scanning: false,
    scanned: false,
    error: null,
    listCount: 0,
    taskCount: 0,
    truncatedLists: [],
    selectedKeys: [],
    importedKeys: [],
    importing: false,
    importResult: null,

    scan: async () => {
      set((state) => ({ googleTasks: { ...state.googleTasks, scanning: true, error: null } }));
      try {
        const data = await googleTasksService.fetchAllTaskLists();
        const candidates = buildImportCandidates(data);
        const taskCount = data.reduce((total, entry) => total + entry.tasks.length, 0);

        set((state) => ({
          googleTasks: {
            ...state.googleTasks,
            candidates,
            listCount: data.length,
            taskCount,
            truncatedLists: data.filter((entry) => entry.truncated).map((entry) => entry.list.title),
            scanning: false,
            scanned: true,
            selectedKeys: [],
            // importedKeys deliberately survives a rescan. A candidate key is
            // listId + normalised title, which is stable as occurrences accumulate,
            // so what was imported before is still identifiable afterwards. Only a
            // rename in Google invalidates it — and the title match against existing
            // chores covers the session-loss case anyway.
            importResult: null,
          },
        }));
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to read Google Tasks';
        set((state) => ({
          googleTasks: { ...state.googleTasks, scanning: false, error: message },
        }));
      }
    },

    reset: () => {
      googleTasksService.disconnect();
      set((state) => ({
        googleTasks: {
          ...state.googleTasks,
          candidates: [],
          scanning: false,
          scanned: false,
          error: null,
          listCount: 0,
          taskCount: 0,
          truncatedLists: [],
          selectedKeys: [],
          importedKeys: [],
          importing: false,
          importResult: null,
        },
      }));
    },

    clearError: () => {
      set((state) => ({ googleTasks: { ...state.googleTasks, error: null } }));
    },

    toggleSelection: (key) => {
      set((state) => {
        const selected = state.googleTasks.selectedKeys.includes(key);
        return {
          googleTasks: {
            ...state.googleTasks,
            selectedKeys: selected
              ? state.googleTasks.selectedKeys.filter((k) => k !== key)
              : [...state.googleTasks.selectedKeys, key],
          },
        };
      });
    },

    setSelection: (keys, selected) => {
      set((state) => {
        const current = new Set(state.googleTasks.selectedKeys);
        // Already-imported candidates are never selectable, so bulk-select skips them.
        const imported = new Set(state.googleTasks.importedKeys);
        for (const key of keys) {
          if (selected && !imported.has(key)) current.add(key);
          if (!selected) current.delete(key);
        }
        return { googleTasks: { ...state.googleTasks, selectedKeys: [...current] } };
      });
    },

    clearSelection: () => {
      set((state) => ({ googleTasks: { ...state.googleTasks, selectedKeys: [] } }));
    },

    importSelected: async (householdId) => {
      const userId = get().auth.user?.uid;
      const { candidates, selectedKeys, importedKeys } = get().googleTasks;

      const selected = candidates.filter(
        (candidate) => selectedKeys.includes(candidate.key) && !importedKeys.includes(candidate.key),
      );
      if (!userId || !householdId || selected.length === 0) return;

      set((state) => ({
        googleTasks: { ...state.googleTasks, importing: true, importResult: null, error: null },
      }));

      const failures: ImportOutcome['failures'] = [];
      const succeeded: string[] = [];

      // Sequential on purpose: it keeps the offline outbox in a sensible order and
      // makes a failure attributable to one task rather than to the batch.
      for (const candidate of selected) {
        const chore = await get().chores.addChore(
          candidate.title,
          candidate.notes ?? '',
          candidate.inference.schedule,
          householdId,
          [{ userId, isResponsible: true }],
          [IMPORT_LABEL],
        );

        // addChore reports failure by returning null rather than throwing. Deliberately not read
        // off chores.error: a chore that was created but could not be queued for sync sets that
        // error too, and counting it as a failure would leave the row ticked for a retry that
        // creates the chore all over again. A chore that exists has been imported, whatever
        // happened to its sync — the error still reaches the user through chores.error.
        if (chore) {
          succeeded.push(candidate.key);
        } else {
          failures.push({ title: candidate.title, error: get().chores.error ?? 'Failed to add chore' });
        }
      }

      set((state) => ({
        googleTasks: {
          ...state.googleTasks,
          importing: false,
          importedKeys: [...state.googleTasks.importedKeys, ...succeeded],
          // Imported rows drop out of the selection; failures stay ticked so a retry
          // doesn't mean picking them all again.
          selectedKeys: state.googleTasks.selectedKeys.filter((key) => !succeeded.includes(key)),
          importResult: { imported: succeeded.length, failures },
        },
      }));
    },

    dismissImportResult: () => {
      set((state) => ({ googleTasks: { ...state.googleTasks, importResult: null } }));
    },
  },
});
